/**
 * @fileoverview Helpers and shared types for the Unify Venue management page.
 *
 * Covers domain types for the two-panel layout:
 * - {@link MarketVenueMappingRecord} — left (Detail) panel, backed by `MarketVenueMappings`.
 * - {@link MasterVenueRecord}        — right (Master) panel, backed by `MasterVenues`.
 */

// ---------------------------------------------------------------------------
// MarketVenueMappingRecord — left (Detail) panel
// ---------------------------------------------------------------------------

/**
 * Shape of a MarketVenueMapping record returned by
 * `GET /MarketVenueMappings/Filter`.
 *
 * The backend performs a left join with `Master_Venues` so that canonical
 * venue data (GoogleUnifiedName, FormattedAddress, PlaceId, ClusterCode,
 * GoogleMapsUri) is included alongside the market-specific fields.
 */
export interface MarketVenueMappingRecord {
    /** React-Admin identifier — string representation of {@link mappingId}. */
    id: string;
    /** Auto-increment PK of the `Market_Venue_Mappings` row. */
    mappingId: number;
    /** FK to the linked `Master_Venues` record (0 = not yet linked). */
    masterVenueId: number;
    /** Numeric market identifier (0 = Primary, 1 = Vivid, 2 = TN, 13 = GC). */
    marketId: number;
    /** Market type string (e.g. "sm", "axs"). */
    marketType: string;
    /** Source venue identifier within the owning market's system. */
    sourceVenueId: number;
    /** Venue display name from the source market's data. */
    marketVenueName: string;
    /** City from the source market's venue record. */
    marketVenueCity: string;
    /** UTC creation timestamp. */
    insertedOn: string | null;
    /** UTC last-update timestamp. */
    updatedOn: string | null;
    /** Email or identifier of the user who last updated this mapping. */
    updatedBy: string | null;

    // Joined from Master_Venues (null when not yet linked):
    /** FK to another MasterVenues row when the linked master venue was merged/replaced. */
    mergedToId: number | null;
    /** Google Places display name of the linked canonical venue. */
    googleUnifiedName: string | null;
    /** Formatted street address of the canonical venue. */
    formattedAddress: string | null;
    /** City from the master venue record. */
    masterVenueCity: string | null;
    /** State abbreviation from the master venue record. */
    state: string | null;
    /** Google Places unique identifier of the canonical venue. */
    placeId: string | null;
    /** Google Maps URL for the canonical venue. */
    googleMapsUri: string | null;
    /** 8-character cluster Plus Code prefix (e.g. "76VVXG2P"). */
    clusterCode: string | null;
    /** Full global Plus Code (e.g. "76VVXG2P+M9"). */
    globalCode: string | null;
    /** Compound Plus Code with locality (e.g. "XG2P+M9 Nashville, TN"). */
    compoundCode: string | null;
    /** Latitude of the canonical venue. */
    lat: number;
    /** Longitude of the canonical venue. */
    long: number;
}

// ---------------------------------------------------------------------------
// MasterVenueRecord — right (Master) panel
// ---------------------------------------------------------------------------

/**
 * Shape of a record returned by `GET /MasterVenues/Filter`.
 *
 * The backend returns `MarketVenueMappingView` rows (Market_Venue_Mappings left-joined
 * with Master_Venues), so each record contains **both** master-venue canonical fields
 * and market-specific mapping fields.  The right panel groups these by `clusterCode`
 * client-side; the expanded panel uses the mapping-specific fields.
 */
export interface MasterVenueRecord {
    /** React-Admin identifier — string representation of the mapping PK. */
    id: string;
    /** Primary key of the `Master_Venues` row. */
    masterVenueId: number;

    // ── Master_Venues canonical fields ─────────────────────────────────────
    /** Google Places display name. */
    googleUnifiedName: string;
    /** Formatted street address from Google Places. */
    formattedAddress: string;
    /** City name. */
    city: string;
    /** State abbreviation. */
    state: string;
    /** Google Places unique identifier. */
    placeId: string;
    /** Google Maps URL. */
    googleMapsUri: string;
    /** Full global Plus Code (e.g. "76VVXG2P+M9"). */
    globalCode: string;
    /** 8-character cluster Plus Code prefix (e.g. "76VVXG2P"). */
    clusterCode: string;
    /** Compound Plus Code with locality (e.g. "XG2P+M9 Nashville, TN"). */
    compoundCode: string;
    /** FK to another MasterVenues row when this record was merged/replaced. */
    mergedToId: number | null;

    // ── Market_Venue_Mappings fields (from the join) ───────────────────────
    /** Numeric market identifier (0 = Primary, 1 = Vivid, 2 = TN, 13 = GC). */
    marketId: number;
    /** Market type string (e.g. "sm", "axs"). */
    marketType: string;
    /** Source venue identifier within the owning market's system. */
    sourceVenueId: number;
    /** Venue display name from the source market's data. */
    marketVenueName: string;
    /** City from the source market's venue record. */
    marketVenueCity: string;
    /** UTC creation timestamp. */
    insertedOn: string | null;
    /** UTC last-update timestamp. */
    updatedOn: string | null;
}

/**
 * Market type value used exclusively for secondary-market (sm_events) venue entries.
 */
export const SM_MARKET_TYPE = 'sm' as const;

/**
 * Lookup table for market ID → display metadata.
 */
export interface MarketTypeName {
    id: number;
    name: string;
    value: string;
}

/**
 * All supported market entries, ordered as they appear in UI controls.
 */
export const MARKET_TYPE_NAMES: MarketTypeName[] = [
    { id: 0,  name: 'Primary',        value: 'Primary'  },
    { id: 1,  name: 'Vivid',          value: 'VV'       },
    { id: 2,  name: 'Ticket Network', value: 'TN'       },
    { id: 13, name: 'Go Ticket',      value: 'GC'       },
];

// ---------------------------------------------------------------------------
// ClusterCode grouping helper (used by MasterGroupedView — right panel)
// ---------------------------------------------------------------------------

/**
 * A group of `MasterVenueRecord`s sharing the same `clusterCode` value.
 *
 * - `clusterCode` is `''` (empty string) for the synthetic "no cluster code" group.
 * - `records` contains all master venues whose `clusterCode` equals this key.
 */
export interface ClusterCodeGroup {
    /** The shared 8-character cluster Plus Code prefix; empty string = no code. */
    clusterCode: string;
    /** All master venue records that belong to this cluster group. */
    records: MasterVenueRecord[];
}

/**
 * Groups a flat array of `MasterVenueRecord`s by their `clusterCode` field.
 *
 * - Records whose `clusterCode` is falsy are placed in a single group keyed
 *   by the empty string `''`.
 * - Named groups are sorted alphabetically; the empty group is always last.
 *
 * @param data - Array of master venue records from react-admin's list context.
 * @returns Ordered array of {@link ClusterCodeGroup} objects.
 *
 * @example
 * const groups = buildClusterCodeGroups(masterVenues);
 * // groups[0].clusterCode === '76VVXG2P'  (first alphabetically)
 * // groups[last].clusterCode === ''         (no-cluster-code group)
 */
export function buildClusterCodeGroups (data: MasterVenueRecord[] | undefined): ClusterCodeGroup[] {
    const map = new Map<string, MasterVenueRecord[]>();

    (data ?? []).forEach(record => {
        const key = record.clusterCode || '';
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(record);
    });

    const entries = Array.from(map.entries());
    entries.sort(([a], [b]) => {
        if (a === '' && b !== '') return 1;
        if (a !== '' && b === '') return -1;
        return a.localeCompare(b);
    });

    return entries.map(([clusterCode, records]) => ({ clusterCode, records }));
}

// ---------------------------------------------------------------------------
// Market ID display helpers
// ---------------------------------------------------------------------------

/**
 * Looks up the display name for a numeric MarketId.
 *
 * @param marketId - Numeric market identifier.
 * @returns The human-readable name, or the raw id as a string if not found.
 */
export function getMarketName (marketId: number | null | undefined): string {
    if (marketId === null || marketId === undefined) return '';
    return MARKET_TYPE_NAMES.find(m => m.id === marketId)?.name ?? String(marketId);
}

export function getMarketType (marketId: number, marketType: string): string {
    if (marketId === null || marketId === undefined) return '';
    if (marketId > 0) { // Secondary market (sm) entries have marketId > 0
        return (
            MARKET_TYPE_NAMES.find(x => x.id === marketId)?.value
                    ?? String(marketId)
        );
    }
    return marketType;
}
